import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apiFetch } from '../hooks/useApi';
import { useAuth } from '../hooks/useAuth';
import MemberFormFields, { type MemberDraft } from '../components/MemberFormFields';
import ChildCollection, { type ChildField } from '../components/ChildCollection';
import type { MemberDetailResponse, MemberWriteResponse, StatusesResponse } from '@shared/api-responses';
import type { Member, MemberStatus } from '@shared/types';

const DATE_PH = 'YYYY-MM-DD';

const EMERGENCY_FIELDS: ChildField[] = [
  { name: 'name', label: 'Name' },
  { name: 'relation', label: 'Relation' },
  { name: 'phone', label: 'Phone' },
  { name: 'address', label: 'Address' },
  { name: 'city', label: 'City' },
  { name: 'state', label: 'State' },
  { name: 'zip', label: 'Zip' },
];
const PHYSICAL_FIELDS: ChildField[] = [
  { name: 'type', label: 'Type' },
  { name: 'date_of_physical', label: 'Date', placeholder: DATE_PH },
  { name: 'fingerprint_date', label: 'Fingerprint date', placeholder: DATE_PH },
];
const PRIOR_SERVICE_FIELDS: ChildField[] = [
  { name: 'start_date', label: 'Start', placeholder: DATE_PH },
  { name: 'end_date', label: 'End', placeholder: DATE_PH },
  { name: 'reason', label: 'Reason' },
  { name: 'total', label: 'Total' },
];
const CERT_FIELDS: ChildField[] = [
  { name: 'cert_type', label: 'Type' },
  { name: 'cert_level', label: 'Level' },
  { name: 'value', label: 'Value' },
  { name: 'completed_date', label: 'Completed', placeholder: DATE_PH },
  { name: 'expires_date', label: 'Expires', placeholder: DATE_PH },
  { name: 'due_date', label: 'Due', placeholder: DATE_PH },
  { name: 'notes', label: 'Notes' },
  { name: 'source', label: 'Source' },
];
const PIN_FIELDS: ChildField[] = [
  { name: 'pin', label: 'Pin' },
  { name: 'date_given', label: 'Date given', placeholder: DATE_PH },
];
const ASSIGNMENT_FIELDS: ChildField[] = [
  { name: 'rank', label: 'Rank' },
  { name: 'battalion', label: 'Battalion' },
  { name: 'gear_issued', label: 'Gear issued' },
  { name: 'apparatus_operator', label: 'Apparatus operator' },
  { name: 'suppression_pos', label: 'Suppression position' },
];

const IMMUTABLE_FIELDS = new Set(['co_id', 'created_at', 'updated_at', 'deleted_at']);

function buildDraft(m: Member): MemberDraft {
  const draft: MemberDraft = {};
  for (const [key, value] of Object.entries(m)) {
    if (IMMUTABLE_FIELDS.has(key)) continue;
    draft[key] = value == null ? '' : String(value);
  }
  return draft;
}

function age(dob: string | null): string {
  if (!dob) return '';
  const d = new Date(dob);
  if (isNaN(d.getTime())) return '';
  const now = new Date();
  let a = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) a--;
  return ` (age ${a})`;
}

function Field({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-zinc-400">{label}</dt>
      <dd className="text-sm text-zinc-800">{value || <span className="text-zinc-300">—</span>}</dd>
    </div>
  );
}

function Section({ title, count, children }: { title: string; count: number; children: ReactNode }) {
  return (
    <section className="rounded-lg border border-border-subtle bg-surface-raised">
      <h2 className="border-b border-border-subtle px-4 py-2.5 text-sm font-semibold text-dcvfd-dark">
        {title} <span className="text-zinc-400 font-normal">({count})</span>
      </h2>
      <div className="p-4">{count === 0 ? <p className="text-sm text-zinc-400">None on file.</p> : children}</div>
    </section>
  );
}

export default function MemberDetail() {
  const { coId } = useParams();
  const { user } = useAuth();
  const canEdit = user?.role === 'editor' || user?.role === 'admin' || user?.role === 'super_admin';

  const [data, setData] = useState<MemberDetailResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<MemberDraft>({});
  const [statuses, setStatuses] = useState<MemberStatus[]>([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Refetch in place (no loading flash) — used after child-section edits.
  const reload = useCallback(() => {
    apiFetch<MemberDetailResponse>(`/members/${coId}`)
      .then(setData)
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load member'));
  }, [coId]);

  useEffect(() => {
    setData(null);
    setError(null);
    setEditing(false);
    reload();
  }, [coId, reload]);

  function startEdit() {
    if (!data) return;
    setDraft(buildDraft(data.member));
    setSaveError(null);
    setEditing(true);
    if (statuses.length === 0) {
      apiFetch<StatusesResponse>('/statuses')
        .then((r) => setStatuses(r.statuses))
        .catch(() => {});
    }
  }

  async function save() {
    setSaving(true);
    setSaveError(null);
    try {
      const res = await apiFetch<MemberWriteResponse>(`/members/${coId}`, { method: 'PUT', body: draft });
      setData((prev) => (prev ? { ...prev, member: res.member } : prev));
      setEditing(false);
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  if (error) return <div className="rounded-lg bg-red-50 text-red-700 px-4 py-3 text-sm">{error}</div>;
  if (!data) return <p className="text-sm text-zinc-400 animate-pulse">Loading…</p>;

  const m = data.member;
  const directory = data.scope === 'directory';
  const setField = (name: string, value: string) => setDraft((d) => ({ ...d, [name]: value }));

  return (
    <div className="space-y-5">
      <div>
        <Link to="/members" className="text-sm text-dcvfd-accent hover:underline">
          ← Members
        </Link>
        <div className="mt-2 flex items-baseline gap-3 flex-wrap">
          <h1 className="text-2xl font-bold text-dcvfd-dark">
            {m.last_name}, {m.first_name}
            {m.aka ? <span className="text-zinc-400 font-normal text-lg"> “{m.aka}”</span> : null}
          </h1>
          <span className="text-zinc-400">CO ID {m.co_id}</span>
          {m.status && (
            <span className="rounded bg-dcvfd-accent/15 px-2 py-0.5 text-sm text-dcvfd-dark">{m.status}</span>
          )}
          {canEdit && !editing && (
            <button
              onClick={startEdit}
              className="ml-auto rounded-lg border border-border-default px-3 py-1.5 text-sm text-dcvfd-dark hover:bg-surface"
            >
              Edit
            </button>
          )}
        </div>
      </div>

      {/* Core fields */}
      {editing ? (
        <section className="rounded-lg border border-border-subtle bg-surface-raised p-4 space-y-4">
          {saveError && <div className="rounded-lg bg-red-50 text-red-700 px-4 py-3 text-sm">{saveError}</div>}
          <MemberFormFields draft={draft} onChange={setField} statuses={statuses} />
          <div className="flex gap-2 border-t border-border-subtle pt-3">
            <button
              onClick={save}
              disabled={saving}
              className="rounded-lg bg-dcvfd-accent px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save changes'}
            </button>
            <button
              onClick={() => setEditing(false)}
              disabled={saving}
              className="rounded-lg border border-border-default px-4 py-2 text-sm hover:bg-surface"
            >
              Cancel
            </button>
          </div>
        </section>
      ) : directory ? (
        <section className="rounded-lg border border-border-subtle bg-surface-raised p-4 space-y-4">
          <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-800">
            Limited view — you can see contact details only. Home address, date of birth, emergency contacts, and
            service history are available to officers. Contact an officer if you need the full record.
          </div>
          <dl className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-4">
            <Field label="Battalion" value={m.battalion} />
            <Field label="Cell" value={m.cell} />
            <Field label="Email" value={m.email} />
            <Field label="Joined" value={m.joined} />
          </dl>
        </section>
      ) : (
        <section className="rounded-lg border border-border-subtle bg-surface-raised p-4">
          <dl className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-4">
            <Field label="DOB" value={m.dob ? `${m.dob}${age(m.dob)}` : ''} />
            <Field label="Sex" value={m.sex} />
            <Field label="Battalion" value={m.battalion} />
            <Field label="Card #" value={m.card_no} />
            <Field label="Address" value={m.address} />
            <Field label="City / State / Zip" value={[m.city, m.state, m.zip].filter(Boolean).join(', ')} />
            <Field label="Phone" value={m.phone} />
            <Field label="Cell" value={m.cell} />
            <Field label="Email" value={m.email} />
            <Field label="Source" value={m.source} />
            <Field label="Area of Interest" value={m.area_interest} />
            <Field label="Joined" value={m.joined} />
            <Field label="Inactive" value={m.inactive} />
            <Field label="Resigned" value={m.resigned} />
            <Field label="Dropped" value={m.drop_date} />
            <Field label="Reason Inactive" value={m.reason_inactive} />
          </dl>
          {m.comments && (
            <div className="mt-4 border-t border-border-subtle pt-3">
              <Field label="Comments" value={<span className="whitespace-pre-wrap">{m.comments}</span>} />
            </div>
          )}
        </section>
      )}

      {!directory && (
        <div className="grid md:grid-cols-2 gap-5">
          <ChildCollection
            title="Emergency Contacts"
            slug="emergency-contacts"
            coId={m.co_id}
            items={data.emergencyContacts}
            fields={EMERGENCY_FIELDS}
            canEdit={canEdit}
            onChanged={reload}
          />

          <ChildCollection
            title="Physicals"
            slug="physicals"
            coId={m.co_id}
            items={data.physicals}
            fields={PHYSICAL_FIELDS}
            canEdit={canEdit}
            onChanged={reload}
          />

          <ChildCollection
            title="Prior Service"
            slug="prior-service"
            coId={m.co_id}
            items={data.priorService}
            fields={PRIOR_SERVICE_FIELDS}
            canEdit={canEdit}
            onChanged={reload}
          />

          <ChildCollection
            title="Certifications"
            slug="certifications"
            coId={m.co_id}
            items={data.certifications}
            fields={CERT_FIELDS}
            canEdit={canEdit}
            onChanged={reload}
          />

          <ChildCollection
            title="Earned Pins"
            slug="earned-pins"
            coId={m.co_id}
            items={data.pins}
            fields={PIN_FIELDS}
            canEdit={canEdit}
            onChanged={reload}
          />

          <ChildCollection
            title="Assignments / Gear"
            slug="assignments"
            coId={m.co_id}
            items={data.assignments}
            fields={ASSIGNMENT_FIELDS}
            canEdit={canEdit}
            onChanged={reload}
          />

          <Section title="LOSAP Service Hours" count={data.serviceHours.length}>
            <ul className="space-y-1 text-sm">
              {data.serviceHours.map((s) => (
                <li key={s.id} className="flex justify-between">
                  <span>{s.year}</span>
                  <span className="text-zinc-500">
                    {s.total_hours ?? 0} hrs
                    {s.needs_validation ? (
                      <span className="ml-1 text-amber-600" title="Needs validation">
                        ⚠
                      </span>
                    ) : null}
                  </span>
                </li>
              ))}
            </ul>
          </Section>
        </div>
      )}
    </div>
  );
}
